import React, { useState, useEffect, Suspense } from "react";
import Select from "react-select";
import Alert from "../../ui/Alert.jsx";
import { customSelectStylesLarge } from "../../../config/selectStyles";
import froalaConfig from "../../../config/froalaConfig.js";
import { ArrowLeft, Save, Package } from "lucide-react";

const FroalaEditor = React.lazy(() => import("react-froala-wysiwyg"));

// Konvertiert Plain-Text-Newlines zu <br>-Tags fuer korrekte Froala-Darstellung
// (entspricht PHP nl2br fuer Alt-Beschreibungen ohne HTML-Tags).
const nl2br = (str) => {
    if (!str) return '';
    if (/<\/?[a-z][\s\S]*?>/i.test(str)) return str;
    return str.replace(/(?:\r\n|\r|\n)/g, '<br>');
};

const ArticleEdit = ({ articleId, onBack, onUpdated }) => {
    const [formData, setFormData] = useState({
        categoryId: '',
        subcategoryId: '',
        categoryNumber: '',
        subcategoryNumber: '',
        articleNumber: '001',
        title: '',
        description: '',
        count: 1,
        unit: '',
        taxType: 'netto',
        tax: '',
        price: ''
    });

    const [dropdownData, setDropdownData] = useState({
        categories: [],
        subcategories: [],
        units: [],
        taxes: []
    });

    const [userSettings, setUserSettings] = useState({
        currency: 'EUR',
        currencySymbol: '€',
        isTaxLiable: true
    });

    const [articlePreview, setArticlePreview] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [alert, setAlert] = useState({ message: "", type: "" });

    useEffect(() => {
        if (articleId) {
            fetchArticleData();
            fetchDropdownData();
        }
    }, [articleId]);

    const fetchArticleData = async () => {
        try {
            setLoading(true);
            const response = await fetch(`index.php/articles/getArticle?id=${articleId}`);
            const article = await response.json();

            if (article.error) {
                setAlert({ message: article.error, type: "danger" });
                setLoading(false);
                return;
            }

            // Get category number
            const categoryNumber = article.categoryId > 0
                ? String(article.categoryId).padStart(2, '0')
                : '';

            // Get subcategory number
            const subcategoryNumber = article.subcategoryId > 0
                ? String(article.subcategoryId).padStart(2, '0')
                : '';

            // Determine price based on netGross setting
            const price = article.netGross === 'brutto'
                ? article.amountIncVatFormatted
                : article.amountFormatted;

            setFormData({
                categoryId: article.categoryId || '',
                subcategoryId: article.subcategoryId || '',
                categoryNumber: categoryNumber,
                subcategoryNumber: subcategoryNumber,
                articleNumber: article.articleNumberSuffix || '001',
                title: article.title || '',
                description: nl2br(article.description || ''),
                count: article.count || 1,
                unit: article.unit || '',
                taxType: article.netGross || 'netto',
                tax: article.tax || '',
                price: price || ''
            });

            // Load subcategories if category is set
            if (article.categoryId) {
                fetchSubcategoriesForCategory(article.categoryId);
                fetchArticlePreview(article.categoryId, article.subcategoryId);
            }

            setLoading(false);
        } catch (error) {
            console.error("Fehler beim Laden des Artikels:", error);
            setAlert({ message: "Fehler beim Laden des Artikels", type: "danger" });
            setLoading(false);
        }
    };

    const fetchDropdownData = async () => {
        try {
            const [categoriesRes, unitsRes, taxesRes, settingsRes] = await Promise.all([
                fetch("index.php/articles/getCategoriesDropdown"),
                fetch("index.php/documents/getUnits"),
                fetch("index.php/documents/getTaxes"),
                fetch("index.php/articles/getUserSettings")
            ]);

            const [categories, units, taxes, settings] = await Promise.all([
                categoriesRes.json(),
                unitsRes.json(),
                taxesRes.json(),
                settingsRes.json()
            ]);

            setDropdownData(prev => ({
                ...prev,
                categories: categories,
                units: units,
                taxes: taxes
            }));

            setUserSettings(settings);

        } catch (error) {
            console.error("Fehler beim Laden der Dropdown-Daten:", error);
            setAlert({ message: "Fehler beim Laden der Daten", type: "danger" });
        }
    };

    const fetchSubcategoriesForCategory = async (categoryId) => {
        try {
            const response = await fetch(`index.php/articles/getSubCategoriesForCategory?categoryId=${categoryId}`);
            const subcategories = await response.json();
            setDropdownData(prev => ({
                ...prev,
                subcategories: subcategories
            }));
        } catch (error) {
            console.error("Fehler beim Laden der Unterkategorien:", error);
        }
    };

    const handleCategoryChange = async (selected) => {
        const categoryId = selected ? selected.value : '';
        const categoryNumber = selected ? selected.number : '';

        setFormData(prev => ({
            ...prev,
            categoryId: categoryId,
            categoryNumber: categoryNumber,
            subcategoryId: '',
            subcategoryNumber: ''
        }));

        if (categoryId) {
            fetchSubcategoriesForCategory(categoryId);
            fetchArticlePreview(categoryId, '');
        } else {
            setDropdownData(prev => ({
                ...prev,
                subcategories: []
            }));
            setArticlePreview([]);
        }
    };

    const handleSubcategoryChange = async (selected) => {
        const subcategoryId = selected ? selected.value : '';
        const subcategoryNumber = selected ? selected.number : '';

        setFormData(prev => ({
            ...prev,
            subcategoryId: subcategoryId,
            subcategoryNumber: subcategoryNumber
        }));

        if (formData.categoryId) {
            fetchArticlePreview(formData.categoryId, subcategoryId);
        }
    };

    const fetchArticlePreview = async (categoryId, subcategoryId) => {
        try {
            let url = `index.php/articles/getArticlePreviewJson?categoryId=${categoryId}`;
            if (subcategoryId) {
                url += `&subcategoryId=${subcategoryId}`;
            }
            const response = await fetch(url);
            const data = await response.json();
            // Filter out current article from preview
            const filteredData = data.filter(a => a.id !== parseInt(articleId));
            setArticlePreview(filteredData);
        } catch (error) {
            console.error("Fehler beim Laden der Artikel-Vorschau:", error);
        }
    };

    const handleDescriptionChange = (content) => {
        setFormData(prev => ({ ...prev, description: content }));
    };

    const formatCurrency = (value) => {
        let cleaned = value.replace(/[^\d,.-]/g, '');
        return cleaned;
    };

    const handlePriceChange = (e) => {
        const formatted = formatCurrency(e.target.value);
        setFormData(prev => ({ ...prev, price: formatted }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setAlert({ message: "", type: "" });

        // Validation
        const errors = [];
        if (!formData.title.trim()) errors.push("Titel ist erforderlich");
        if (!formData.price.trim()) errors.push("Preis ist erforderlich");
        if (userSettings.isTaxLiable && !formData.tax) errors.push("MwSt. ist erforderlich");

        if (errors.length > 0) {
            setAlert({ message: errors.join(", "), type: "danger" });
            return;
        }

        setSaving(true);

        try {
            const postData = {
                id: articleId,
                title: formData.title,
                description: formData.description,
                categoryId: formData.categoryId || 0,
                subcategoryId: formData.subcategoryId || 0,
                articleNumber: formData.articleNumber,
                taxType: userSettings.isTaxLiable ? formData.taxType : 'netto',
                tax: userSettings.isTaxLiable ? formData.tax : 1,
                price: formData.price,
                unit: formData.unit || 1,
                count: formData.count || 1
            };

            const response = await fetch('index.php/articles/updateArticle', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(postData)
            });

            const result = await response.json();

            if (result.success) {
                setAlert({ message: result.message, type: "success" });
                if (onUpdated) {
                    onUpdated();
                }
                setTimeout(() => {
                    onBack();
                }, 1500);
            } else {
                setAlert({ message: result.error || "Fehler beim Aktualisieren", type: "danger" });
                setSaving(false);
            }

        } catch (error) {
            console.error("Fehler beim Aktualisieren:", error);
            setAlert({ message: "Ein Fehler ist aufgetreten", type: "danger" });
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Lädt...</span>
                </div>
            </div>
        );
    }

    return (
        <div>
            {/* Back link */}
            <button
                onClick={onBack}
                style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.375rem',
                    fontSize: '0.875rem',
                    color: 'var(--text-secondary, #64748b)',
                    marginBottom: '0.75rem',
                    cursor: 'pointer',
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    transition: 'color 0.2s ease'
                }}
                onMouseEnter={(e) => e.currentTarget.style.color = 'var(--accent-slate, #5b7c8d)'}
                onMouseLeave={(e) => e.currentTarget.style.color = 'var(--text-secondary, #64748b)'}
            >
                <ArrowLeft size={16} />
                Zurück zur Liste
            </button>

            {/* Page Header */}
            <div className="mb-4">
                <h1 className="h2 fw-bold text-dark d-flex align-items-center gap-2 mb-2">
                    <Package size={32} className="icon-articles" />
                    Artikel bearbeiten
                </h1>
                <p className="text-muted mb-0">
                    {formData.title || 'Artikel'}
                </p>
            </div>

            {/* Form Card */}
            <div style={{
                background: 'var(--bg-card, #ffffff)',
                border: '1px solid var(--border-light, #e2e8f0)',
                borderRadius: '12px',
                overflow: 'hidden',
                boxShadow: '0 1px 3px rgba(44, 62, 80, 0.04), 0 1px 2px rgba(44, 62, 80, 0.06)'
            }}>
                {/* Action Bar */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    padding: '1rem 1.5rem',
                    borderBottom: '1px solid var(--border-light, #e2e8f0)',
                    background: 'var(--bg-secondary, #f5f7f9)'
                }}>
                    <button
                        type="submit"
                        form="editArticleForm"
                        disabled={saving}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            padding: '0.5rem 1rem',
                            borderRadius: '8px',
                            background: 'var(--accent-slate, #5b7c8d)',
                            border: '1px solid var(--accent-slate, #5b7c8d)',
                            color: '#ffffff',
                            fontSize: '0.875rem',
                            fontWeight: '500',
                            cursor: saving ? 'not-allowed' : 'pointer',
                            opacity: saving ? 0.7 : 1
                        }}
                    >
                        <Save size={16} />
                        {saving ? "Wird gespeichert..." : "Speichern"}
                    </button>
                </div>

                {/* Form Body */}
                <div style={{ padding: '1.5rem' }}>
                    {alert.message && (
                        <Alert
                            type={alert.type}
                            message={alert.message}
                            onClose={() => setAlert({ message: "", type: "" })}
                        />
                    )}

                    <form id="editArticleForm" onSubmit={handleSubmit}>
                        <div className="row">
                            {/* Left Column - Form */}
                            <div className="col-md-7">
                                {/* Category Selection */}
                                <div className="row mb-3">
                                    <div className="col-md-6">
                                        <label className="form-label" style={{
                                            fontWeight: '500',
                                            color: 'var(--text-primary, #2c3e50)',
                                            marginBottom: '0.5rem'
                                        }}>
                                            Kategorie
                                        </label>
                                        <Select
                                            options={dropdownData.categories}
                                            styles={customSelectStylesLarge}
                                            menuPortalTarget={document.body}
                                            menuPosition="fixed"
                                            placeholder="Kategorie auswählen..."
                                            isClearable
                                            value={dropdownData.categories.find(c => c.value === formData.categoryId) || null}
                                            onChange={handleCategoryChange}
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label" style={{
                                            fontWeight: '500',
                                            color: 'var(--text-primary, #2c3e50)',
                                            marginBottom: '0.5rem'
                                        }}>
                                            Unterkategorie
                                        </label>
                                        <Select
                                            options={dropdownData.subcategories}
                                            styles={customSelectStylesLarge}
                                            menuPortalTarget={document.body}
                                            menuPosition="fixed"
                                            placeholder="Unterkategorie auswählen..."
                                            isClearable
                                            isDisabled={!formData.categoryId}
                                            value={dropdownData.subcategories.find(s => s.value === formData.subcategoryId) || null}
                                            onChange={handleSubcategoryChange}
                                        />
                                    </div>
                                </div>

                                {/* Number Fields */}
                                <div className="row mb-3">
                                    <div className="col-md-4">
                                        <label className="form-label" style={{
                                            fontWeight: '500',
                                            color: 'var(--text-primary, #2c3e50)',
                                            marginBottom: '0.5rem'
                                        }}>
                                            Kategorie-Nr.
                                        </label>
                                        <input
                                            type="text"
                                            className="form-control bg-light"
                                            value={formData.categoryNumber}
                                            readOnly
                                            style={{
                                                borderRadius: '8px',
                                                border: '1px solid var(--border-medium, #cbd5e1)',
                                                padding: '0.625rem 0.875rem'
                                            }}
                                        />
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label" style={{
                                            fontWeight: '500',
                                            color: 'var(--text-primary, #2c3e50)',
                                            marginBottom: '0.5rem'
                                        }}>
                                            Unterkategorie-Nr.
                                        </label>
                                        <input
                                            type="text"
                                            className="form-control bg-light"
                                            value={formData.subcategoryNumber}
                                            readOnly
                                            style={{
                                                borderRadius: '8px',
                                                border: '1px solid var(--border-medium, #cbd5e1)',
                                                padding: '0.625rem 0.875rem'
                                            }}
                                        />
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label" style={{
                                            fontWeight: '500',
                                            color: 'var(--text-primary, #2c3e50)',
                                            marginBottom: '0.5rem'
                                        }}>
                                            Artikelnummer
                                        </label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={formData.articleNumber}
                                            onChange={(e) => setFormData(prev => ({ ...prev, articleNumber: e.target.value }))}
                                            style={{
                                                borderRadius: '8px',
                                                border: '1px solid var(--border-medium, #cbd5e1)',
                                                padding: '0.625rem 0.875rem'
                                            }}
                                        />
                                    </div>
                                </div>

                                {/* Title */}
                                <div className="mb-3">
                                    <label className="form-label" style={{
                                        fontWeight: '500',
                                        color: 'var(--text-primary, #2c3e50)',
                                        marginBottom: '0.5rem'
                                    }}>
                                        Titel *
                                    </label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={formData.title}
                                        onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                                        placeholder="Artikelbezeichnung"
                                        style={{
                                            borderRadius: '8px',
                                            border: '1px solid var(--border-medium, #cbd5e1)',
                                            padding: '0.625rem 0.875rem'
                                        }}
                                    />
                                </div>

                                {/* Description */}
                                <div className="mb-3">
                                    <label className="form-label" style={{
                                        fontWeight: '500',
                                        color: 'var(--text-primary, #2c3e50)',
                                        marginBottom: '0.5rem'
                                    }}>
                                        Beschreibung
                                    </label>
                                    <div style={{
                                        border: '1px solid var(--border-medium, #cbd5e1)',
                                        borderRadius: '8px',
                                        overflow: 'hidden'
                                    }}>
                                        <Suspense fallback={<div style={{ padding: '1rem', color: 'var(--text-secondary)' }}>Lade Editor...</div>}>
                                            <FroalaEditor
                                                tag="textarea"
                                                config={{
                                                    ...froalaConfig,
                                                    height: 150,
                                                    placeholderText: 'Beschreibung eingeben...'
                                                }}
                                                model={formData.description}
                                                onModelChange={handleDescriptionChange}
                                            />
                                        </Suspense>
                                    </div>
                                </div>

                                {/* Count and Unit */}
                                <div className="row mb-3">
                                    <div className="col-md-6">
                                        <label className="form-label" style={{
                                            fontWeight: '500',
                                            color: 'var(--text-primary, #2c3e50)',
                                            marginBottom: '0.5rem'
                                        }}>
                                            Anzahl
                                        </label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            value={formData.count}
                                            onChange={(e) => setFormData(prev => ({ ...prev, count: parseInt(e.target.value) || 1 }))}
                                            min="1"
                                            style={{
                                                borderRadius: '8px',
                                                border: '1px solid var(--border-medium, #cbd5e1)',
                                                padding: '0.625rem 0.875rem'
                                            }}
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label" style={{
                                            fontWeight: '500',
                                            color: 'var(--text-primary, #2c3e50)',
                                            marginBottom: '0.5rem'
                                        }}>
                                            Einheit
                                        </label>
                                        <Select
                                            options={dropdownData.units}
                                            styles={customSelectStylesLarge}
                                            menuPortalTarget={document.body}
                                            menuPosition="fixed"
                                            placeholder="Einheit auswählen..."
                                            value={dropdownData.units.find(u => u.value === formData.unit) || null}
                                            onChange={(selected) => setFormData(prev => ({
                                                ...prev,
                                                unit: selected ? selected.value : ''
                                            }))}
                                        />
                                    </div>
                                </div>

                                {/* Tax Type, Tax Rate, Price */}
                                <div className="row mb-3">
                                    {userSettings.isTaxLiable && (
                                        <>
                                            <div className="col-md-4">
                                                <label className="form-label" style={{
                                                    fontWeight: '500',
                                                    color: 'var(--text-primary, #2c3e50)',
                                                    marginBottom: '0.5rem'
                                                }}>
                                                    Steuerart
                                                </label>
                                                <Select
                                                    options={[
                                                        { value: 'netto', label: 'Netto' },
                                                        { value: 'brutto', label: 'Brutto' }
                                                    ]}
                                                    styles={customSelectStylesLarge}
                                            menuPortalTarget={document.body}
                                            menuPosition="fixed"
                                                    value={{ value: formData.taxType, label: formData.taxType === 'netto' ? 'Netto' : 'Brutto' }}
                                                    onChange={(selected) => setFormData(prev => ({
                                                        ...prev,
                                                        taxType: selected ? selected.value : 'netto'
                                                    }))}
                                                />
                                            </div>
                                            <div className="col-md-4">
                                                <label className="form-label" style={{
                                                    fontWeight: '500',
                                                    color: 'var(--text-primary, #2c3e50)',
                                                    marginBottom: '0.5rem'
                                                }}>
                                                    MwSt. *
                                                </label>
                                                <Select
                                                    options={dropdownData.taxes}
                                                    styles={customSelectStylesLarge}
                                            menuPortalTarget={document.body}
                                            menuPosition="fixed"
                                                    placeholder="MwSt. auswählen..."
                                                    value={dropdownData.taxes.find(t => t.value === formData.tax) || null}
                                                    onChange={(selected) => setFormData(prev => ({
                                                        ...prev,
                                                        tax: selected ? selected.value : ''
                                                    }))}
                                                />
                                            </div>
                                        </>
                                    )}
                                    <div className={userSettings.isTaxLiable ? "col-md-4" : "col-12"}>
                                        <label className="form-label" style={{
                                            fontWeight: '500',
                                            color: 'var(--text-primary, #2c3e50)',
                                            marginBottom: '0.5rem'
                                        }}>
                                            Preis ({formData.taxType === 'brutto' ? 'Brutto' : 'Netto'}) *
                                        </label>
                                        <div className="input-group">
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={formData.price}
                                                onChange={handlePriceChange}
                                                placeholder="0,00"
                                                style={{
                                                    borderRadius: '8px 0 0 8px',
                                                    border: '1px solid var(--border-medium, #cbd5e1)',
                                                    padding: '0.625rem 0.875rem'
                                                }}
                                            />
                                            <span className="input-group-text" style={{
                                                borderRadius: '0 8px 8px 0',
                                                background: 'var(--bg-secondary, #f5f7f9)',
                                                border: '1px solid var(--border-medium, #cbd5e1)',
                                                borderLeft: 'none'
                                            }}>{userSettings.currency}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Right Column - Article Preview */}
                            <div className="col-md-5">
                                <div style={{
                                    background: 'var(--bg-secondary, #f5f7f9)',
                                    borderRadius: '12px',
                                    padding: '1rem',
                                    height: '100%',
                                    minHeight: '400px'
                                }}>
                                    <h6 style={{
                                        fontWeight: '600',
                                        color: 'var(--text-primary, #2c3e50)',
                                        marginBottom: '1rem',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.5rem'
                                    }}>
                                        <Package size={18} />
                                        Weitere Artikel in dieser Kategorie
                                    </h6>

                                    {articlePreview.length === 0 ? (
                                        <div style={{
                                            textAlign: 'center',
                                            color: 'var(--text-tertiary, #94a3b8)',
                                            padding: '2rem 1rem'
                                        }}>
                                            <Package size={32} style={{ opacity: 0.5, marginBottom: '0.5rem' }} />
                                            <p style={{ margin: 0, fontSize: '0.875rem' }}>
                                                {formData.categoryId
                                                    ? 'Keine weiteren Artikel in dieser Kategorie'
                                                    : 'Kategorie auswählen, um Artikel anzuzeigen'}
                                            </p>
                                        </div>
                                    ) : (
                                        <div style={{ maxHeight: '350px', overflowY: 'auto' }}>
                                            <table className="table table-sm" style={{ fontSize: '0.8125rem' }}>
                                                <thead>
                                                    <tr>
                                                        <th style={{ fontWeight: '600', color: 'var(--text-secondary, #64748b)' }}>Titel</th>
                                                        <th style={{ fontWeight: '600', color: 'var(--text-secondary, #64748b)', textAlign: 'right' }}>Preis</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {articlePreview.map((article) => (
                                                        <tr key={article.id}>
                                                            <td style={{ color: 'var(--text-primary, #2c3e50)' }}>
                                                                {article.title}
                                                            </td>
                                                            <td style={{ color: 'var(--text-primary, #2c3e50)', textAlign: 'right' }}>
                                                                {article.amountFormatted} {userSettings.currency}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default ArticleEdit;
